import { useRef, useState, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Bold, Italic, Underline, List, ListOrdered, Link2, ChevronLeft, ChevronRight, Image as ImageIcon, Trash2, ArrowUp, ArrowDown, Plus, Video, Images, Type, Loader2, Heading } from "lucide-react";
import axios from "@/lib/api";
import { getApiUrl } from "@/lib/api";

const API = `${getApiUrl()}/api`;

// Convert a YouTube/Vimeo URL to an embeddable src.
export function toEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (u.hostname.includes("youtube.com")) {
      const id = u.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}`;
      if (u.pathname.startsWith("/embed/")) return url;
    }
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
  } catch (e) { /* ignore */ }
  return url;
}

// Upload an image file to Cloudinary via the backend signature flow.
async function uploadImage(file) {
  const sig = (await axios.get(`${API}/cloudinary/signature?folder=gamepop/tutorials`)).data;
  const fd = new FormData();
  fd.append("file", file);
  fd.append("api_key", sig.api_key);
  fd.append("timestamp", sig.timestamp);
  fd.append("signature", sig.signature);
  fd.append("folder", sig.folder);
  if (sig.transformation) fd.append("transformation", sig.transformation);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, { method: "POST", body: fd });
  if (!res.ok) throw new Error("Upload failed");
  const data = await res.json();
  return data.secure_url;
}

/* ------------------- READ-ONLY RENDERER ------------------- */
function Slideshow({ images }) {
  const [ref, embla] = useEmblaCarousel({ loop: true });
  const [selected, setSelected] = useState(0);
  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelected(embla.selectedScrollSnap());
    embla.on("select", onSelect);
    onSelect();
    return () => embla.off("select", onSelect);
  }, [embla]);
  if (!images?.length) return null;
  const currentCaption = images[selected]?.caption;
  return (
    <div className="rounded-xl overflow-hidden border border-white/10 bg-black/40">
      <div className="relative">
        <div className="overflow-hidden" ref={ref}>
          <div className="flex">
            {images.map((img, i) => (
              <div key={i} className="min-w-0 flex-[0_0_100%]">
                <img src={img.url} alt={img.caption || ""} className="w-full max-h-[520px] object-contain bg-black" />
              </div>
            ))}
          </div>
        </div>
        {images.length > 1 && (
          <>
            <button onClick={() => embla && embla.scrollPrev()} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 rounded-full p-1.5 text-white" data-testid="slideshow-prev">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={() => embla && embla.scrollNext()} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 rounded-full p-1.5 text-white" data-testid="slideshow-next">
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-1.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => embla && embla.scrollTo(i)}
                  className={`rounded-full transition-all ${i === selected ? "w-4 h-2 bg-white" : "w-2 h-2 bg-white/40 hover:bg-white/70"}`}
                  aria-label={`Go to slide ${i + 1}`}
                  data-testid={`slideshow-dot-${i}`}
                />
              ))}
            </div>
            <div className="absolute top-2 right-2 text-[11px] px-2 py-0.5 rounded-full bg-black/60 text-white/80" data-testid="slideshow-counter">
              {selected + 1} / {images.length}
            </div>
          </>
        )}
      </div>
      {currentCaption && (
        <p className="text-center text-sm text-white/70 px-4 py-3 whitespace-pre-wrap break-words" data-testid="slideshow-caption">
          {currentCaption}
        </p>
      )}
    </div>
  );
}

export function TutorialRenderer({ blocks = [] }) {
  return (
    <div className="space-y-6">
      {blocks.map((b, i) => {
        if (b.type === "text") {
          return <div key={i} className="tutorial-prose text-white/85 leading-relaxed" dangerouslySetInnerHTML={{ __html: b.html || "" }} />;
        }
        if (b.type === "image") {
          return (
            <figure key={i} className="space-y-1">
              <img src={b.url} alt={b.caption || ""} className="w-full rounded-xl border border-white/10" />
              {b.caption && <figcaption className="text-center text-xs text-white/50">{b.caption}</figcaption>}
            </figure>
          );
        }
        if (b.type === "video") {
          const src = toEmbedUrl(b.url);
          return (
            <div key={i} className="space-y-1">
              <div className="relative w-full rounded-xl overflow-hidden border border-white/10" style={{ paddingTop: "56.25%" }}>
                <iframe src={src} title={`video-${i}`} className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
              {b.caption && <p className="text-center text-xs text-white/50">{b.caption}</p>}
            </div>
          );
        }
        if (b.type === "slideshow") return <Slideshow key={i} images={b.images || []} />;
        return null;
      })}
    </div>
  );
}

/* ------------------- RICH TEXT (contentEditable) ------------------- */
function RichText({ html, onChange }) {
  const ref = useRef(null);
  // Set the initial HTML ONCE on mount. Never re-bind via React on every
  // keystroke (that resets the caret to the start → text types "backwards").
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (html || "")) {
      ref.current.innerHTML = html || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const exec = (cmd) => {
    document.execCommand(cmd, false, null);
    if (ref.current) onChange(ref.current.innerHTML);
    ref.current?.focus();
  };
  const addLink = () => {
    const url = window.prompt("Link URL (https://...)");
    if (url) { document.execCommand("createLink", false, url); if (ref.current) onChange(ref.current.innerHTML); }
  };
  const toggleHeading = () => {
    // Toggle the current block between an <h3> heading and a normal paragraph.
    const sel = window.getSelection();
    const node = sel && sel.anchorNode ? (sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement) : null;
    const inHeading = node && node.closest && node.closest("h1,h2,h3,h4");
    document.execCommand("formatBlock", false, inHeading ? "P" : "H3");
    if (ref.current) onChange(ref.current.innerHTML);
    ref.current?.focus();
  };
  const Btn = ({ onClick, children, label }) => (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={label}
      className="p-1.5 rounded hover:bg-white/10 text-white/70">{children}</button>
  );
  return (
    <div className="rounded-lg border border-white/10 bg-black/30">
      <div className="flex items-center gap-0.5 px-2 py-1 border-b border-white/10 flex-wrap">
        <Btn onClick={() => exec("bold")} label="Bold"><Bold className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("italic")} label="Italic"><Italic className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("underline")} label="Underline"><Underline className="w-4 h-4" /></Btn>
        <Btn onClick={toggleHeading} label="Heading"><Heading className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("insertUnorderedList")} label="Bulleted list"><List className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("insertOrderedList")} label="Numbered list"><ListOrdered className="w-4 h-4" /></Btn>
        <Btn onClick={addLink} label="Insert link"><Link2 className="w-4 h-4" /></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        className="tutorial-prose min-h-[100px] px-3 py-2 text-sm text-white/90 focus:outline-none"
        data-testid="richtext-editor"
      />
    </div>
  );
}

export function ImageUploadButton({ onUploaded, label = "Upload image", multiple = false }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const url = await uploadImage(f);
        onUploaded(url);
      }
    } catch (err) { /* eslint-disable-line */ } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" multiple={multiple} className="hidden" onChange={handle} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
        className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-purple-500/20 border border-purple-400/30 text-purple-100 hover:bg-purple-500/30 disabled:opacity-60">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />} {label}
      </button>
    </>
  );
}

/* ------------------- BLOCK EDITOR ------------------- */
export function BlockEditor({ blocks, onChange }) {
  const update = (i, patch) => onChange(blocks.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const remove = (i) => onChange(blocks.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const copy = [...blocks];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };
  const add = (type) => {
    const base = type === "text" ? { type, html: "" }
      : type === "image" ? { type, url: "", caption: "" }
      : type === "video" ? { type, url: "", caption: "" }
      : { type: "slideshow", images: [], caption: "" };
    onChange([...blocks, base]);
  };

  return (
    <div className="space-y-3">
      {blocks.map((b, i) => (
        <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-3 space-y-2" data-testid={`block-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wide text-white/40">{b.type}</span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => move(i, -1)} className="p-1 rounded hover:bg-white/10 text-white/50" title="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => move(i, 1)} className="p-1 rounded hover:bg-white/10 text-white/50" title="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => remove(i)} className="p-1 rounded hover:bg-red-500/20 text-red-300" title="Delete block" data-testid={`block-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>

          {b.type === "text" && <RichText html={b.html} onChange={(html) => update(i, { html })} />}

          {b.type === "image" && (
            <div className="space-y-2">
              {b.url ? <img src={b.url} alt="" className="max-h-52 rounded border border-white/10" /> : <p className="text-xs text-white/40">No image yet.</p>}
              <div className="flex items-center gap-2">
                <ImageUploadButton onUploaded={(url) => update(i, { url })} />
                <input value={b.caption || ""} onChange={(e) => update(i, { caption: e.target.value })} placeholder="Caption (optional)" className="flex-1 text-xs bg-black/30 border border-white/10 rounded px-2 py-1 text-white/80" />
              </div>
            </div>
          )}

          {b.type === "video" && (
            <div className="space-y-2">
              <input value={b.url || ""} onChange={(e) => update(i, { url: e.target.value })} placeholder="YouTube or Vimeo URL" className="w-full text-xs bg-black/30 border border-white/10 rounded px-2 py-1 text-white/80" data-testid={`video-url-${i}`} />
              <input value={b.caption || ""} onChange={(e) => update(i, { caption: e.target.value })} placeholder="Caption (optional)" className="w-full text-xs bg-black/30 border border-white/10 rounded px-2 py-1 text-white/80" />
            </div>
          )}

          {b.type === "slideshow" && (
            <div className="space-y-2">
              <div className="space-y-2">
                {(b.images || []).map((img, k) => (
                  <div key={k} className="flex items-start gap-2 rounded border border-white/10 bg-black/20 p-2">
                    <div className="relative shrink-0">
                      <img src={img.url} alt="" className="h-20 w-20 object-cover rounded border border-white/10" />
                      <button type="button" onClick={() => update(i, { images: b.images.filter((_, x) => x !== k) })} className="absolute -top-1.5 -right-1.5 bg-red-500 rounded-full p-0.5 text-white" data-testid={`slide-remove-${k}`}><Trash2 className="w-3 h-3" /></button>
                    </div>
                    <textarea
                      value={img.caption || ""}
                      onChange={(e) => update(i, { images: b.images.map((im, x) => x === k ? { ...im, caption: e.target.value } : im) })}
                      placeholder="Caption text (shown under this image)"
                      rows={3}
                      className="flex-1 resize-y rounded bg-white/5 border border-white/10 px-2.5 py-1.5 text-sm text-white/80 placeholder:text-white/30 focus:outline-none focus:border-white/30"
                      data-testid={`slide-caption-${k}`}
                    />
                  </div>
                ))}
              </div>
              <ImageUploadButton multiple label="Add slide images" onUploaded={(url) => update(i, { images: [...(b.images || []), { url, caption: "" }] })} />
            </div>
          )}
        </div>
      ))}

      <div className="flex items-center gap-2 flex-wrap pt-1">
        <span className="text-xs text-white/40">Add block:</span>
        <button type="button" onClick={() => add("text")} className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded bg-white/5 border border-white/10 text-white/70 hover:bg-white/10" data-testid="add-block-text"><Type className="w-3.5 h-3.5" /> Text</button>
        <button type="button" onClick={() => add("image")} className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded bg-white/5 border border-white/10 text-white/70 hover:bg-white/10" data-testid="add-block-image"><ImageIcon className="w-3.5 h-3.5" /> Image</button>
        <button type="button" onClick={() => add("video")} className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded bg-white/5 border border-white/10 text-white/70 hover:bg-white/10" data-testid="add-block-video"><Video className="w-3.5 h-3.5" /> Video</button>
        <button type="button" onClick={() => add("slideshow")} className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded bg-white/5 border border-white/10 text-white/70 hover:bg-white/10" data-testid="add-block-slideshow"><Images className="w-3.5 h-3.5" /> Slideshow</button>
      </div>
    </div>
  );
}

export { Plus };
